/**
 * AppBreadcrumbs - Navigation breadcrumbs for authenticated routes
 *
 * Displays a breadcrumb trail based on the current route path.
 * Uses Portuguese labels. Only renders on authenticated (non-public) pages.
 */

import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';

/**
 * Map of path segments to human-readable Portuguese labels
 */
const PATH_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  patients: 'Pacientes',
  new: 'Novo Paciente',
  calendar: 'Agenda',
  sessions: 'Sessões',
  settings: 'Configurações',
  reports: 'Relatórios',
  import: 'Importar Dados',
  export: 'Exportar Dados',
  insurance: 'Convênios',
  team: 'Equipe',
  roles: 'Permissões',
  profile: 'Meu Perfil',
  subscription: 'Assinatura',
  referrals: 'Indicações',
  notifications: 'Notificações',
  documents: 'Documentos',
  templates: 'Biblioteca de Templates',
  branding: 'Personalização',
  'waiting-list': 'Fila de Espera',
  'online-booking': 'Agendamento Online',
  checkout: 'Checkout',
};

/**
 * Routes that are considered authenticated (should show breadcrumbs)
 */
const AUTHENTICATED_PREFIXES = [
  '/dashboard',
  '/patients',
  '/calendar',
  '/sessions',
  '/settings',
  '/reports',
  '/import',
  '/export',
  '/insurance',
  '/team',
  '/roles',
  '/profile',
  '/subscription',
  '/referrals',
  '/notifications',
  '/documents',
  '/templates',
  '/branding',
  '/waiting-list',
  '/online-booking',
  '/checkout',
];

/**
 * Check if a segment looks like a dynamic ID (UUID or similar)
 */
const isDynamicSegment = (segment: string): boolean => {
  // UUID pattern or long alphanumeric strings
  return /^[0-9a-f-]{8,}$/i.test(segment) || /^\d+$/.test(segment);
};

const AppBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;

  // Only show on authenticated routes
  const isAuthenticatedRoute = AUTHENTICATED_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );
  if (!isAuthenticatedRoute) return null;

  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  // Build breadcrumb items
  const items: { label: string; path: string; isLast: boolean }[] = [];
  let accumulatedPath = '';

  segments.forEach((segment, index) => {
    accumulatedPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    let label: string;
    if (isDynamicSegment(segment)) {
      label = 'Detalhes';
    } else {
      label = PATH_LABELS[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    items.push({ label, path: accumulatedPath, isLast });
  });

  return (
    <Box sx={{ mb: 2, mt: 0.5 }}>
      <Breadcrumbs
        aria-label="navegação"
        separator=">"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 0.5,
            color: 'text.secondary',
          },
        }}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/dashboard"
          underline="hover"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.85rem',
          }}
        >
          <HomeIcon sx={{ fontSize: 18 }} />
          Início
        </Link>

        {/* Dynamic segments */}
        {items.map((item) =>
          item.isLast ? (
            <Typography
              key={item.path}
              color="text.primary"
              sx={{ fontSize: '0.85rem', fontWeight: 500 }}
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              {item.label}
            </Link>
          )
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default AppBreadcrumbs;
